import Page from "../framework/page";
import Server from "../server";
import {App} from "../app/app";
import View, {Notification} from "../framework/view";
import template from "../../pugc/wrb-page";
import {
    FunctionBadge,
    HashUrl,
    ROLE_ADMIN,
    ROLE_CASUAL_DELIVERY,
    ROLE_DELIVERY,
    ROLE_FLORIST,
    Template
} from "../misc/types";
import modal from "../misc/modal";
import {plural, windowHashUrl} from "../misc/utils";
import LocalDB from "../misc/localdb";
import jQuery from "jquery";


export class BadgeNotification extends Notification {
    b: FunctionBadge;

    constructor(b: FunctionBadge) {
        super("OrdersBadgeNotification");
        this.b = b;
    }
}

type FunctionLink = {
    section: string,
    name: string,
    url?: HashUrl,
    fullurl?: string,
    roles?: string[],
    prune?: boolean,
    hasBadges?: boolean,
    target?: string,
};

export default class WrbPage extends Page {

    static classname = "wrb-page";

    private readonly server = Server.instance();

    private app: App;

    private content?: View;

    /**
     * Some functions don't need the full page template. This is set from the App when the content is set.
     */
    customTemplate?: Template;

    private badges : { [key: HashUrl] : FunctionBadge } = {};

    // --------------------

    constructor(app: App) {
        super(".page", WrbPage.classname);
        this.app = app;

        // Inits...
        modal();

        this.initBadges();
    }

    async initBadges() {
        const user = await this.server.getUser();

        if (! user || ! user.role) {
            return;
        }

        // Notifications...
        const links = this.getFunctionLinks();

        const l = links
            .filter(f => f.roles && f.roles.indexOf(user.role as string) >= 0 && f.hasBadges);

        // Pre-instantiate views
        for (const link of l) {
            if (link.url) {
                const fn = this.app.functionFromUrl(link.url, user);
                const v = fn.view();
                v.setParent(this);
            }
        }
    }

    setContent(content: View) {
        this.content = content;
        content.setParent(this);
    }

    protected render0(): JQuery<HTMLElement> {

        const data = this.getRenderData();

        const out = this.pug(this.customTemplate || template, data);

        this.afterUpdate(() => {
            const hashUrl = data.hashUrl;
            const badge = data.badges && data.badges[hashUrl];
            const title = badge && badge.red ? `(${badge.red} new ${plural(badge.red, "order", "orders")})` : "West Row Botanica";
            jQuery("head title").text(title);
        });

        return out;
    }

    getFunctionLinks() {

        const eventDates = LocalDB.instance().get("event_dates");
        const deliveryMapDates = LocalDB.instance().get("delivery_map_dates");

        const functionLinks: FunctionLink[] = [
            {
                section: "Orders",
                name: "New Orders",
                url: "/orders/new",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                hasBadges: true,
            },
            {
                section: "Orders",
                name: "Take order",
                url: "/orders/add",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
            {
                section: "Orders",
                name: "Take payment",
                url: "/payment",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
            {
                section: "Orders",
                name: "Write card",
                url: "/orders/card",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
            {
                section: "Orders",
                name: "Tax Invoice",
                fullurl: "api/tax-invoice",
                target: "_blank",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
            /*
            {
                section: "Orders",
                name: "Record payment",
                url: "/orders/add/payment",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
             */
            {
                section: "Orders",
                name: "Today's orders",
                url: "/orders/today",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                hasBadges: true,
            },
            {
                section: "Orders",
                name: `Event deliveries`,
                url: `/orders/all/deliveries/${eventDates}`,
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                prune: ! eventDates,
                hasBadges: true,
            },
            {
                section: "Orders",
                name: `Event delivery map`,
                url: `/orders/map/deliveries/${deliveryMapDates}`,
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                prune: ! deliveryMapDates
            },
            {
                section: "Orders",
                name: `Shop - event`,
                url: `/orders/new/pickups/${eventDates}`,
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                prune: ! eventDates
            },
            {
                section: "Orders",
                name: `Manual orders - event`,
                url: `/orders/all/manual/${eventDates}`,
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
                prune: ! eventDates,
                hasBadges: true,
            },
            {
                section: "Orders",
                name: "All orders",
                url: "/orders",
                roles: [ ROLE_ADMIN, ROLE_FLORIST ],
            },
            {
                section: "Orders",
                name: "Sessions",
                url: "/sessions",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Orders",
                name: "Inventory",
                url: "/inventory",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Delivery",
                name: "Scan",
                url: "/scan",
                roles: [ ROLE_ADMIN, ROLE_FLORIST, ROLE_DELIVERY ],
            },
            {
                section: "Delivery",
                name: "Order number",
                url: "/number",
                roles: [ ROLE_ADMIN, ROLE_FLORIST, ROLE_DELIVERY ],
            },
            {
                section: "Delivery",
                name: "Ready for delivery",
                url: "/pickups",
                roles: [ ROLE_ADMIN, ROLE_FLORIST, ROLE_DELIVERY ],
            },
            {
                section: "Delivery",
                name: "Deliveries",
                url: "/deliveries",
                roles: [ ROLE_ADMIN, ROLE_DELIVERY, ROLE_CASUAL_DELIVERY ],
            },
            {
                section: "Delivery",
                name: "Map",
                url: "/orders/map",
                roles: [ ROLE_ADMIN, ROLE_DELIVERY ],
            },
            {
                section: "Delivery",
                name: "List",
                url: "/orders/list",
                roles: [ ROLE_ADMIN, ROLE_DELIVERY ],
            },
            {
                section: "Admin",
                name: "Users",
                url: "/users",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Products",
                url: "/shop/products",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Collections",
                url: "/shop/collections",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Website",
                url: "/website/edit",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Backup/restore",
                url: "/backup",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Activity",
                url: "/activity",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Configuration",
                url: "/config",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Reports",
                url: "/reports",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Adjust handwriting",
                url: "/handwriting/fix",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "Card prints",
                url: "/cardprint",
                roles: [ ROLE_ADMIN ],
            },
            {
                section: "Admin",
                name: "About",
                url: "/about",
                roles: [ ROLE_ADMIN ],
            },
        ].filter(f => ! f.prune);

        const user = this.app.getUser();
        if (user) {
            functionLinks.push({
                section: "Logout",
                name: "Log out",
                url: "/logout",
                roles: [ user.role as string ]
            });
        } else {
            functionLinks.push({
                section: "Logout",
                name: "Log in",
                url: "/login"
            });
        }

        return functionLinks;
    }

    getRenderData() {

        const functionLinks = this.getFunctionLinks();

        const data = {
            content: this.content,
            functionLinks: functionLinks,
            user: this.app.getUser(),
            hashUrl: windowHashUrl(),
            badges: this.badges,
        };

        return data;
    }

    notify(n: Notification) {
        super.notify(n);
        if (n instanceof PrintOrdersNotification) {
            this.app.gotoUrl("/orders/print/" + n.ids.join(","));
        }
        if (n instanceof CardNotification) {
            this.app.gotoUrl("/orders/card/" + n.ids.join(","));
        }
        if (n instanceof MapOrdersNotification) {
            this.app.gotoUrl("/orders/map/" + n.ids.join(","));
        }
        if (n instanceof InvoiceNotification) {
            this.app.gotoUrl("/orders/invoice/" + n.id);
        }
        if (n instanceof ListOrdersNotification) {
            this.app.gotoUrl("/orders/list/" + n.ids.join(","));
        }
        if (n instanceof ViewOrderNotification) {
            this.app.gotoUrl("/orders/view/" + n.id);
        }
        if (n instanceof EditOrderNotification) {
            this.app.gotoUrl("/orders/edit/" + n.id);
        }
        if (n instanceof EditWebsiteNotification) {
            this.app.gotoUrl("/website/edit");
        }
        if (n instanceof EditPageNotification) {
            this.app.gotoUrl(`/website/edit/page/${encodeURIComponent(n.path)}`);
        }
        if (n instanceof EditProductNotification) {
            this.app.gotoUrl(`/shop/product/${encodeURIComponent(n.name)}`);
        }
        if (n instanceof LogoutNotification) {
            this.app.setUser(undefined);
            this.app.gotoUrl("/login");
        }
        if (n instanceof NewEventDatesNotification) {
            this.update(".sidebar .function-links");
        }
        if (n instanceof BadgeNotification) {
            const url = n.b.fn.url();
            this.badges[url] = n.b;
            this.update(".sidebar .function-links");
        }
    }

    toggleSidebar() {
        this.elt().find(".sidebar").toggleClass("expanded");
    }
    collapseSidebar() {
        this.elt().find(".sidebar").removeClass("expanded");
    }
}

View.on(
    "." + WrbPage.classname,
    "click",
    ".app-sidebar__toggle",
    function(that: WrbPage, event, elt) {
        event.preventDefault();
        that.toggleSidebar();
    });

View.on(
    "." + WrbPage.classname,
    "click",
    ".app-content",
    function(that: WrbPage, event, elt) {
        that.collapseSidebar();
    });

View.on(
    "." + WrbPage.classname,
    "click",
    ".update-avail",
    function(that: WrbPage, event, elt) {
        window.location.reload();
    });

export class PrintOrdersNotification extends Notification {
    ids: string[];
    constructor(ids: string[]) {
        super("PrintOrdersNotification");
        this.ids = ids;
    }
}

export class CardNotification extends Notification {
    ids: string[];
    constructor(ids: string[]) {
        super("CardNotification");
        this.ids = ids;
    }
}
export class MapOrdersNotification extends Notification {
    ids: string[];
    constructor(ids: string[]) {
        super("MapOrdersNotification");
        this.ids = ids;
    }
}
export class InvoiceNotification extends Notification {
    id: string;
    constructor(id: string) {
        super("InvoiceNotification");
        this.id = id;
    }
}
export class ListOrdersNotification extends Notification {
    ids: string[];
    constructor(ids: string[]) {
        super("ListOrdersNotification");
        this.ids = ids;
    }
}
export class ViewOrderNotification extends Notification {
    id: string;
    constructor(id: string) {
        super("ViewOrderNotification");
        this.id = id;
    }
}
export class EditOrderNotification extends Notification {
    id: string;
    constructor(id: string) {
        super("EditOrderNotification");
        this.id = id;
    }
}

export class LogoutNotification extends Notification {
    constructor() {
        super("LogoutNotification");
    }
}

export class NewEventDatesNotification extends Notification {
    constructor() {
        super("NewEventDatesNotification");
    }
}
export class EditWebsiteNotification extends Notification {
    constructor() {
        super("EditWebsiteNotification");
    }
}
export class EditPageNotification extends Notification {
    path: string;
    constructor(path: string) {
        super("EditPageNotification");
        this.path = path;
    }
}
export class EditMenuNotification extends Notification {
    name: string;
    constructor(name: string) {
        super("EditMenuNotification");
        this.name = name;
    }
}

export class EditProductNotification extends Notification {
    name: string;
    constructor(name: string) {
        super("EditProductNotification");
        this.name = name;
    }
}
